import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { isSameDay } from '@core/utils/date.utils';
import { DragObjectTypeEnum } from '../../enums/drag-object-type.enum';
import { DayModel } from '../../models/day-model';
import { DriverVehiclePairModel } from '../../models/driver-vehicle-pair.model';
import { OrderDragModel, OrderDroppedEvent } from '../../models/order-drag-and-drop.model';
import { normalizeOrderDates, OrderModel } from '../../models/order.model';
import { RouteModel } from '../../models/route.model';
import { PlannerDraggingManagerService } from '../../services/planner-draggin-manager.service';
import { AddNewAbsenceEvent } from '../empty-day-box/empty-day-box.component';
import { BusinessObjectType } from '@core/enums/business-object-type.enum';


@Component({
  selector: 'routes-box',
  standalone: false,
  templateUrl: './routes-box.component.html',
  styleUrls: ['./routes-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoutesBoxComponent implements OnChanges {
  @Input() day!: DayModel;
  @Input() driver!: DriverVehiclePairModel;
  @Input() routes!: RouteModel[];

  @Output() orderDropped = new EventEmitter<OrderDroppedEvent>;
  @Output() editRoute = new EventEmitter<RouteModel>;
  @Output() editShipment = new EventEmitter<{ shipment: OrderModel, route: RouteModel }>;
  @Output() showConflicts = new EventEmitter<DayModel>;
  @Output() sendRouteToDriver = new EventEmitter<RouteModel>();
  @Output() addNewAbsence = new EventEmitter<AddNewAbsenceEvent>;

  isValidDragOver = false;
  dragNotValid = false;
  isDragOverNewRoute = false;
  idOfRouteTheDragIsOver: number | null = null;
  destroyRef = inject(DestroyRef);
  showNewRouteBox = true;
  newRoutePossible = true;
  dragRegionNotValid = false;

  constructor(private readonly _dragManager: PlannerDraggingManagerService) {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['routes']) {
      this.newRoutePossible = !this.routes.some(r => r.IsRouteAsDriverAsistant);

      // przeliczanie property w tym miejscu, bo potrzebujemy wiedzieć który aktulanie dzień jest wyświetlany
      for (let route of this.routes) {
        if (route.IsMultiDayRoute) {
          route.IsOneOfFallowingDaysOfMultiDayRoute = route.routeId > 0 && !isSameDay(route.PlanTime, this.day.date);
        }
      }
    }
  }

  onDragOnRouteEnter($event: DragEvent, route: RouteModel) {
    $event.preventDefault();

    if (route.IsPhantomRoute || route.IsRouteAsDriverAsistant) {
      return;
    }

    const routeId = this._dragManager.getDragRouteId($event, DragObjectTypeEnum.SHIPMENT );
    const isValidObjDrag = this._dragManager.isShipmentDraging($event);

    if (!this.dragRegionNotValid && isValidObjDrag && (routeId === null || routeId !== route.routeId)) {
      this.idOfRouteTheDragIsOver = route.routeId;
    }
  }

  onDragOnRouteOver($event: any, route: RouteModel) {
    $event.preventDefault();

    if (route.IsPhantomRoute || route.IsRouteAsDriverAsistant) {
      return;
    }

    const routeId = this._dragManager.getDragRouteId($event, DragObjectTypeEnum.SHIPMENT);
    const isValidObjDrag = this._dragManager.isShipmentDraging($event);

    if (!this.dragRegionNotValid && isValidObjDrag && (routeId === null || routeId !== route.routeId)) {
      this.idOfRouteTheDragIsOver = route.routeId;
    }
  }

  onDragOnRouteLeave() {
    this.idOfRouteTheDragIsOver = null;

  }

  onDragOnNewRouteEnter($event: any) {
    $event.preventDefault();
    this.isDragOverNewRoute = true;
  }

  onDragOnNewRouteOver($event: any) {
    $event.preventDefault();
  }

  onDragOnNewRouteLeave(_: any) {
    this.isDragOverNewRoute = false;
  }

  onDragMainEnter($event: DragEvent) {
    $event.preventDefault();
    this.dragRegionNotValid = !this._dragManager.isDragRegionValid($event, DragObjectTypeEnum.SHIPMENT, this.driver.vehicleRegionId);
    if (!this.dragRegionNotValid) {
      this.isValidDragOver = true;
    }
  }

  onDragMainOver($event: any) {
    $event.preventDefault();
  }

  onDragMainLeave($event: any) {
    const target = $event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const isInside =
      $event.clientX >= rect.left &&
      $event.clientX <= rect.right &&
      $event.clientY >= rect.top &&
      $event.clientY <= rect.bottom;

    if (!isInside) {
      this.resetDragProps();
    }
  }

  onDrop($event: any, route?: RouteModel) {
    $event.preventDefault();

    if (this.dragRegionNotValid || route?.IsPhantomRoute || route?.IsRouteAsDriverAsistant) {
      this.resetDragProps();
      return;
    }

    this.resetDragProps();
    const orderDragInfo = this._dragManager.getDragInfo($event, DragObjectTypeEnum.SHIPMENT);
    const routeDropIsValid = this.isDragOverRouteValid(orderDragInfo, route);

    if (routeDropIsValid && orderDragInfo) {
      this.orderDropped.emit({
        dragInfo: { ...orderDragInfo, order: normalizeOrderDates(orderDragInfo.order) },
        dropInfo: {
          day: this.day,
          driver: this.driver,
          route: route,
        }
      });
    }
  }

  onAddAbsenceClick(objectType: BusinessObjectType, objectId: number): void {
      this.addNewAbsence.emit({ objectType, objectId });
  }

  resetDragProps(): void {
    this.isValidDragOver = false;
    this.dragRegionNotValid = false;
    this.dragNotValid = false;
    this.isDragOverNewRoute = false;
    this.idOfRouteTheDragIsOver = null;
  }

  private isDragOverRouteValid(orderDragInfo: OrderDragModel | null, route: RouteModel | undefined) {
    // rzucamy z zasobnika na widok planera 
    if (orderDragInfo && !route) {
      return true;
    }

    // rzucamy z zasobnika na widok planera, ale na istniejąca trasę 
    if (orderDragInfo && !orderDragInfo.route && route) {
      return true;
    }

    // rzucamy z istniejącej trasy na inną trasę  - sprawdzamy czy nie rzucamy w ramach jednej trasy 
    if (orderDragInfo && orderDragInfo.route && route) {
      return orderDragInfo.route.routeId !== route.routeId;
    }

    return false;
  }
}



